<template>
  <main class="development-competency-detail-page">
    <CompanyBreadcrumb :aria-label="$t('general.competency_detail')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <default-loading v-if="loading"></default-loading>

    <div v-else-if="notFound" class="card empty-state">
      <i class="uil-exclamation-circle"></i>
      <h3>{{ $t('general.competency_not_found') }}</h3>
      <p>{{ $t('general.admin_competency_not_found_hint') }}</p>
      <router-link
        :to="{
          name: 'company.development-evaluation.user',
          params: { userId: currentUserId },
        }"
        class="btn btn-default"
      >
        {{ $t('general.back_to_user_competencies') }}
      </router-link>
    </div>

    <template v-else-if="assignment">
      <section v-if="user" class="card user-context-card">
        <div class="context-card-header">
          <div class="context-card-main">
            <AvatarOrIcon :alt="user.full_name || $t('general.user')"
                          :size-px="60"
                          :src="user.avatar || ''"
            />
            <div class="context-user-info">
              <span class="context-user-label">{{ $t('general.evaluated_user') }}</span>
              <strong class="context-user-name">{{ user.full_name || '-' }}</strong>
              <small class="context-user-org">{{ userOrganization }}</small>
            </div>
          </div>
          <router-link
            :to="{
              name: 'company.development-evaluation.user',
              params: { userId: currentUserId },
            }"
            class="context-card-btn"
          >
            <i class="uil-arrow-left"></i>
            {{ $t('general.all_competencies') }}
          </router-link>
        </div>
      </section>

      <AlertMessage v-if="errorMessage" :message="{ type: 'error', text: errorMessage }" @remove="errorMessage = ''" />
      <AlertMessage v-if="successMessage" :message="{ type: 'success', text: successMessage }" @remove="successMessage = ''" />

      <div class="detail-view-wrapper">
        <AssignedCompetencyDetail
          viewer="admin"
          :assignment="assignment"
          :comments="comments"
          :can-comment="canComment"
          :allow-comment-visibility="true"
          :submitting="submittingComment"
          @submit-comment="submitAdminComment"
        />
      </div>
    </template>
  </main>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import AlertMessage from "@/view/components/notification/AlertMessage.vue";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import AssignedCompetencyDetail from "@/view/components/developmentCompetencies/AssignedCompetencyDetail.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";

export default {
  name: "DevelopmentEvaluationCompetencyDetail",
  components: {
    AlertMessage,
    AssignedCompetencyDetail,
    AvatarOrIcon,
    CompanyBreadcrumb,
    DefaultLoading,
  },
  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
    userCompetencyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      user: null,
      assignment: null,
      comments: [],
      loading: true,
      submittingComment: false,
      notFound: false,
      errorMessage: "",
      successMessage: "",
      requestSequence: 0,
    };
  },
  computed: {
    breadcrumbItems() {
      const items = [
        { name: this.$t("general.development_evaluation"), route: { name: "company.development-evaluation.index" } },
      ];
      if (this.user) {
        items.push({
          name: this.user.full_name || this.$t("general.user"),
          route: { name: "company.development-evaluation.user", params: { userId: this.currentUserId } },
        });
      }
      items.push({ name: (this.assignment && this.assignment.name) || this.$t("general.competency_detail") });
      return items;
    },
    currentUserId() {
      return this.userId || this.$route.params.userId;
    },
    currentCompetencyId() {
      return this.userCompetencyId || this.$route.params.userCompetencyId;
    },
    canComment() {
      return this.$hasPermission("read_competency");
    },
    userOrganization() {
      if (!this.user) return "-";
      return (
        [this.user.department_name, this.user.title_name]
          .filter(Boolean)
          .join(" / ") ||
        this.user.email ||
        "-"
      );
    },
  },
  watch: {
    currentUserId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.loadDetail();
      }
    },
    currentCompetencyId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.loadDetail();
      }
    },
  },
  mounted() {
    this.loadDetail();
  },
  beforeDestroy() {
    this.requestSequence += 1;
  },
  methods: {
    async loadDetail(showLoader = true) {
      if (!this.currentUserId || !this.currentCompetencyId) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      const requestId = ++this.requestSequence;
      if (showLoader) this.loading = true;
      this.notFound = false;
      this.errorMessage = "";

      try {
        // Detay ve kullanıcı özeti; kullanıcı bilgisi liste endpoint'inden alınıyor
        const [detailResponse, userResponse] = await Promise.all([
          ApiService.get(
            `api/competencies-evaluation/${this.currentUserId}/${this.currentCompetencyId}`
          ),
          ApiService.get(
            `api/competencies-evaluation/${this.currentUserId}?${querystring.stringify(
              { page: 1, perPage: 1 }
            )}`
          ),
        ]);

        if (requestId !== this.requestSequence) return;

        const payload = detailResponse?.data || {};
        const item = payload.data || payload;
        if (!item || item.id == null) {
          this.notFound = true;
          return;
        }

        this.assignment = {
          id: item.id,
          name: item.name,
          description: item.description,
          assigned_at: item.assigned_at,
          updated_at: item.updated_at,
          comments_count: item.comments_count,
          latest_target: item.latest_target ?? null,
        };
        this.comments = Array.isArray(item.comments) ? item.comments : [];
        this.user =
          userResponse?.data?.user || this.user || { id: this.currentUserId };
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        if (error?.response?.status === 404) {
          this.notFound = true;
        } else {
          this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.competency_detail_load_error");
        }
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    findOwnComment() {
      const list = Array.isArray(this.comments) ? this.comments : [];
      const byCanUpdate = list.find(
        (item) => item && item.type !== "target" && item.can_update === true
      );
      if (byCanUpdate) return byCanUpdate;

      const authUserId = this.authUser?.id;
      if (authUserId == null) return null;
      return (
        list.find(
          (item) =>
            item &&
            item.type !== "target" &&
            item.user &&
            String(item.user.id) === String(authUserId)
        ) || null
      );
    },
    async submitAdminComment(text, entryType = "comment", visibility = "public") {
      if (this.submittingComment) return;

      const comment = String(text || "").trim();
      this.errorMessage = "";
      this.successMessage = "";

      if (entryType !== "comment") {
        this.errorMessage = this.$t("general.admin_comment_only");
        return;
      }
      if (!comment) {
        this.errorMessage = this.$t("general.comment_required");
        return;
      }

      // Normal yorum kişi başına bir tanedir; kendi yorumu varsa PUT ile güncellenir
      const isUpdate = !!this.findOwnComment();
      const body = {
        comment,
        visibility: visibility === "private" ? "private" : "public",
      };
      this.submittingComment = true;

      try {
        const url = `api/competencies-evaluation/${this.currentUserId}/${this.currentCompetencyId}/comment`;
        if (isUpdate) {
          await ApiService.put(url, body);
        } else {
          await ApiService.post(url, body);
        }

        await this.loadDetail(false);
        this.successMessage = isUpdate
          ? this.$t("general.admin_comment_updated")
          : this.$t("general.admin_comment_added");
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.comment?.[0] ||
          error?.message ||
          this.$t("general.comment_save_failed");
      } finally {
        this.submittingComment = false;
      }
    },
  },
};
</script>

<style scoped>
.development-competency-detail-page {
  --cd-primary: var(--primaryColor, #3e416d);
  --cd-text: #1e293b;
  --cd-muted: #64748b;
  --cd-border: #e2e8f0;
  margin: 0;
  max-width: 1180px;
  width: 100%;
  min-width: 0;
}

/* ── User context card ── */
.user-context-card {
  border: 1px solid var(--cd-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.context-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  background: linear-gradient(135deg, #f7faff 0%, #fff 70%);
  border-bottom: 1px solid #edf1f5;
}

.context-card-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.user-context-card :deep(.avatar) {
  border: 3px solid #e3ebf5;
  flex: 0 0 60px;
  border-radius: 50%;
}

.context-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.context-user-label {
  color: #8b96a7;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}

.context-user-name {
  color: #273142;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.context-user-org {
  color: #718096;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.context-card-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 9px 16px;
  border: 1px solid var(--cd-border);
  border-radius: 10px;
  background: #fff;
  color: var(--cd-text);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.context-card-btn:hover {
  border-color: var(--cd-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.08);
  color: var(--cd-primary);
  text-decoration: none;
}

/* ── Detail wrapper ── */
.detail-view-wrapper {
  margin-top: 18px;
  position: relative;
}

/* ── Empty state ── */
.empty-state {
  border: 1px solid var(--cd-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  color: #778295;
  padding: 64px 20px;
  text-align: center;
}

.empty-state i {
  color: #a9b6c7;
  font-size: 50px;
}

.empty-state h3 {
  color: #3d4756;
  margin: 12px 0 5px;
}

.empty-state .btn {
  margin-top: 10px;
}

/* ════════════════════════════════════════════
   Tablet / Small — max 959px
   ════════════════════════════════════════════ */
@media (max-width: 959px) {
  .context-card-header {
    padding: 18px 16px;
    gap: 14px;
  }

  .context-card-main {
    gap: 12px;
  }

  .context-user-name {
    font-size: 17px;
  }

  .context-card-btn {
    min-height: 44px;
    padding: 10px 16px;
    font-size: 14px;
  }

  .detail-view-wrapper {
    margin-top: 14px;
  }
}

/* ════════════════════════════════════════════
   Mobile — max 767px
   ════════════════════════════════════════════ */
@media (max-width: 767px) {
  .development-competency-detail-page {
    padding: 0 0 24px;
  }

  .context-card-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 14px;
    padding: 20px 16px;
    background: linear-gradient(180deg, #f7faff 0%, #fff 100%);
  }

  .context-card-main {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .user-context-card :deep(.avatar) {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
  }

  .context-user-label {
    font-size: 11px;
  }

  .context-user-name {
    font-size: 16px;
    white-space: normal;
    word-break: break-word;
    text-overflow: unset;
    overflow: visible;
  }

  .context-user-org {
    font-size: 13px;
    white-space: normal;
    word-break: break-word;
    text-overflow: unset;
    overflow: visible;
  }

  .context-card-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 15px;
    font-weight: 600;
    justify-content: center;
  }

  .context-card-btn i {
    display: none;
  }

  .detail-view-wrapper {
    margin-top: 12px;
  }

  .empty-state {
    padding: 40px 16px;
  }

  .empty-state i {
    font-size: 40px;
  }

  .empty-state h3 {
    font-size: 17px;
  }

  .empty-state p {
    font-size: 14px;
  }

  .empty-state .btn {
    min-height: 48px;
    padding: 12px 24px;
    font-size: 15px;
    width: 100%;
    text-align: center;
    border-radius: 10px;
  }
}

/* ════════════════════════════════════════════
   Hover & reduced motion
   ════════════════════════════════════════════ */
@media (hover: none) {
  .development-competency-detail-page :deep(.btn-hover:hover) {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .development-competency-detail-page * {
    transition: none !important;
  }
}
</style>
